import { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { useConfigStore, useDestinationsStore, useRegionStore } from '@packages/stores';
import { DestinationsForm, FormOnChange, TripDestination } from '@packages/types';
import { validateDestinations, validateDestinationsSubmitButton } from '@packages/utils';

import { Notification } from '@elements/notification';
import { usePriceChangeModal } from '@hooks';
import { i18n } from '@i18n';
import { useTripStore } from '@store';
import { sendAnalyticsEvent, testProps } from '@utils';

import { Container } from '../container';
import { DestinationsFooter } from '../destinationsFooter';
import { DestinationsNote } from '../destinationsNote';
import { FooterButtonGroup } from '../footerButtonGroup';
import { GetQuoteProvider } from '../getQuoteProvider';
import { SearchDestination, SelectedDestination, TrendingDestinations } from '../searchDestination';

export type SelectDestinationsProps = {
  onBack: () => Promise<void> | void;
  onNext: () => Promise<void> | void;
  isModal?: boolean;
};

export const SelectDestinations = (props: SelectDestinationsProps) => {
  return (
    <GetQuoteProvider
      defaultFormValues={{ destinations: [] }}
      mode="onBlur"
      reValidateMode="onChange">
      <SelectDestinationsContent {...props} />
    </GetQuoteProvider>
  );
};

export const SelectDestinationsContent = ({
  onBack,
  onNext,
  isModal = false,
}: SelectDestinationsProps) => {
  const tripStoreDestinations = useTripStore(state => state.trip?.destinations ?? []);
  // form state
  const { setValue, handleSubmit, control, watch } = useFormContext<DestinationsForm>();
  const formTripDestinations = watch('destinations');
  // global state
  const country = useRegionStore(state => state.region?.country);
  const regionSpecificConfig = useConfigStore(state => state?.regionSpecificConfig);

  const updateTripDestinations = useTripStore(state => state.setTripDestinations);
  const updateUserSearchedDestinations = useDestinationsStore(
    state => state.setUserSearchedDestinations,
  );
  const syncTrip = useTripStore(state => state.syncTrip);
  const { openPriceChangeModal } = usePriceChangeModal();

  useEffect(() => {
    setValue('destinations', tripStoreDestinations);
  }, [setValue, tripStoreDestinations]);

  // local state

  const [isLoading, setIsLoading] = useState(false);
  const maxDestinations = regionSpecificConfig?.RULES.MAX_DESTINATIONS ?? 10;
  const hasReachedMaxDestinations = formTripDestinations.length >= maxDestinations;
  useEffect(() => {
    if (isModal) {
      sendAnalyticsEvent('Modal viewed', { modalTitle: i18n.t('selectDestinations.title') });
    }
  }, [isModal]);

  const sendToAnalytics = useCallback((destination: TripDestination, isAdded: boolean) => {
    const eventName = isAdded ? 'Destination Added' : 'Destination Removed';
    sendAnalyticsEvent(eventName, { destination: destination.longName });
  }, []);

  const handleToggleDestination = useCallback(
    (onFormChange: FormOnChange) => (destination: TripDestination) => {
      const isDestinationSelected = formTripDestinations.find(
        formDestination => formDestination.countryCode == destination.countryCode,
      );

      if (
        (hasReachedMaxDestinations && !isDestinationSelected) ||
        typeof destination !== 'object'
      ) {
        return;
      }

      //if the formTripDestination is empty or new destination does not match, add it
      if (!formTripDestinations || !isDestinationSelected) {
        onFormChange([...formTripDestinations, destination]);
        sendToAnalytics(destination, true);
      } else {
        // remove from user searched destinations
        onFormChange(
          formTripDestinations.filter(dest => dest.countryCode !== destination.countryCode),
        );
        sendToAnalytics(destination, false);
      }
    },
    [formTripDestinations, hasReachedMaxDestinations, sendToAnalytics],
  );

  const onSubmit = async (data: DestinationsForm) => {
    if (isModal) {
      const isDestinationsValid = validateDestinationsSubmitButton({
        isModal,
        formTripDestinations: formTripDestinations,
        tripDestinations: tripStoreDestinations,
      });

      if (isDestinationsValid) {
        return onBack();
      }
      // confirmation modal
      return openPriceChangeModal({
        onNext: async () => {
          setIsLoading(true);
          updateUserSearchedDestinations(formTripDestinations);
          updateTripDestinations(data.destinations);
          onNext();
          await syncTrip();
          setIsLoading(false);
        },
      });
    }

    setIsLoading(true);
    updateUserSearchedDestinations(formTripDestinations);
    updateTripDestinations(data.destinations);
    await onNext();
    setIsLoading(false);
  };

  return (
    <Controller
      name="destinations"
      control={control}
      rules={{
        validate: {
          required: v => !!v.length,
          validateDestinations: v => validateDestinations(v, maxDestinations),
        },
      }}
      render={({ field: { onChange }, fieldState: { error } }) => {
        return (
          <Container
            titleText={i18n.t('selectDestinations.title')}
            className="flex flex-col flex-1"
            hasRegionResidencyStatus={country === 'AU' && !isModal}>
            <div>
              {error?.type === 'required' && (
                <Notification
                  variant="error"
                  header={i18n.t('errors.destination.empty.title')}
                  body={i18n.t('errors.destination.empty.description')}
                  className="mb-5"
                />
              )}
              <SearchDestination
                selectedDestinations={formTripDestinations}
                onClick={(destination: TripDestination) => {
                  handleToggleDestination(onChange)(destination);
                }}
                hasReachedMaxDestinations={hasReachedMaxDestinations}
              />
            </div>
            <SelectedDestination
              selectedDestinations={formTripDestinations}
              onRemoveSelectedDestination={handleToggleDestination(onChange)}
              hasReachedMaxDestinations={hasReachedMaxDestinations}
            />
            <TrendingDestinations
              selectedDestinations={formTripDestinations}
              onToggleDestination={handleToggleDestination(onChange)}
            />
            <DestinationsNote />
            <hr className="border-gray-200 my-6" />
            <div
              className={twMerge('flex flex-col gap-4', isModal && 'flex-col md:flex-row-reverse')}>
              {isModal ? (
                <FooterButtonGroup
                  nextButtonProps={{
                    variant: 'primary',
                    title: i18n.t('selectDestinations.modal.actions.update'),
                    onClick: handleSubmit(onSubmit),
                    isLoading,
                    className: 'w-full',
                    ...testProps('btn-update'),
                  }}
                  backButtonProps={{
                    variant: 'secondary',
                    title: i18n.t('selectDestinations.modal.actions.cancel'),
                    onClick: onBack,
                    className: 'w-full',
                    ...testProps('btn-back'),
                  }}
                />
              ) : (
                <DestinationsFooter
                  isLoading={isLoading}
                  onNext={handleSubmit(onSubmit)}
                  onBack={onBack}
                />
              )}
            </div>
          </Container>
        );
      }}
    />
  );
};
